import React, { useEffect, useState, useRef, useCallback } from 'react';
import { RotateCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { dataService } from '@rti/shared';
import { useData } from '@/contexts/DataContext';

interface FeedstockRow {
  feedstock: string;
  date1: string;
  date2: string;
  date3?: string;
  value1: string;
  value2: string;
  value3?: string;
  change: string;
  feedstockKey?: string;
}

const formatSpotDateUTC = (dateStr?: string) => {
  if (!dateStr) return '';

  const datePart = String(dateStr).split('T')[0]; // Extract just the date part
  const [y, m, d] = datePart.split('-').map(Number);
  if (isNaN(y) || isNaN(m) || isNaN(d) || y < 1900 || m < 1 || m > 12 || d < 1 || d > 31) return '';
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

const formatContractDateUTC = (dateStr?: string) => {
  if (!dateStr) return '';
  const datePart = String(dateStr).split('T')[0]; // Extract just the date part
  const [y, m, d] = datePart.split('-').map(Number);
  if (isNaN(y) || isNaN(m) || isNaN(d) || y < 1900 || m < 1 || m > 12 || d < 1 || d > 31) return '';
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' }).replace(' ', " '");
};

const FeedstockDaily: React.FC = () => {
  const navigate = useNavigate();
  const [feedstockData, setFeedstockData] = useState<FeedstockRow[]>([]);
  const [contractData, setContractData] = useState<FeedstockRow[]>([]);
  const [needsLoading, setNeedsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [spotHeader, setSpotHeader] = useState<{ date1: string; date2: string }>({ date1: '', date2: '' });
  const [contractHeader, setContractHeader] = useState<{ date1: string; date2: string; date3: string }>({ date1: '', date2: '', date3: '' });

  // Track mounted state to avoid setting state after unmount
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // Guard against overlapping fetches
  const isFetchingRef = useRef(false);

  // Get global refresh status to listen for data refresh completion
  const { globalRefreshStatus } = useData();

  // Navigation handlers
  const handleSpotPriceClick = () => {
    navigate('/feedstock-details?feedstock=spot');
  };

  const handleContractPriceClick = () => {
    navigate('/feedstock-details?feedstock=contract');
  };

  const isWeekday = useCallback((dateStr: string): boolean => {
    const datePart = String(dateStr).split('T')[0];
    const [y, m, d] = datePart.split('-').map(Number);
    if (isNaN(y) || isNaN(m) || isNaN(d)) return false;
    const dt = new Date(Date.UTC(y, m - 1, d));
    const dayOfWeek = dt.getUTCDay();
    return dayOfWeek !== 0 && dayOfWeek !== 6;
  }, []);

  // Helper function to load data (supports silent refresh)
  const loadData = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = !!opts?.silent;

    // Prevent overlapping calls
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    if (!silent) {
      setNeedsLoading(true);
      setError(null);
    }

    // Helper to process API response to FeedstockRow[]
    const processData = (response: any, isContract = false): FeedstockRow[] => {
      if (!Array.isArray(response) || response.length < 2) return [];
      // Sort by ISO date string descending to avoid timezone parsing issues
      const sorted = response
        .filter(r => r && typeof r.date === 'string' && (isContract || isWeekday(r.date)))
        .slice()
        .sort((a, b) => String(b.date).localeCompare(String(a.date)));
      const [latest, secondLatest, thirdLatest] = [sorted[0], sorted[1], sorted[2]];
      let allKeys = Object.keys(latest).filter(k => k !== 'date');
      if (isContract) allKeys = allKeys.filter(k => k.includes('_contract'));
      const feedstockSort = [
        'spot_ethylene','ethane','propane','spot_rgp','spot_pgp','spot_benzene','spot_styrene','spot_butadiene','wti_crude_oil','brent_crude_oil','nat_gas','euro_to_us','us_to_ca','vam'
      ];
      const sortedFeedstocks = allKeys.sort((a, b) => {
        const indexA = feedstockSort.indexOf(a);
        const indexB = feedstockSort.indexOf(b);
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        return a.localeCompare(b);
      });
      const formatDate = (date: string) => isContract ? formatContractDateUTC(date) : formatSpotDateUTC(date);
      return sortedFeedstocks.map(feedstock => {
        if (feedstock === 'last_updated' || feedstock.includes('_ngas')) return null;
        let nameForDisplay = isContract ? feedstock.replace('_contract', '') : feedstock;
        let formattedFeedstock = nameForDisplay
          .replace(/_/g, ' ')
          .replace(/us/g, '$US')
          .replace(/ca/g, 'CA$')
          .replace(/rgp/g, 'RGP')
          .replace(/pgp/g, 'PGP')
          .replace(/vam/g, 'VAM')
          .replace(/wti/g, 'WTI')
          .replace(/\b\w/g, char => char.toUpperCase());
        const formatValue = (value: any, key: string) => {
          if (key && key.includes('us')) return '$' + parseFloat(value).toFixed(3);
          return value > 1 ? '$' + parseFloat(value).toFixed(2) : '$' + parseFloat(value).toFixed(4);
        };
        const formatChange = (v1: any, v2: any) => {
          if (v1 != null && v2 != null) {
            const change = v1 - v2;
            if (change === 0) return 'flat';
            const absChange = Math.abs(change);
            const decimalPart = absChange.toString().split('.')[1] || '';
            const decimalPlaces = decimalPart.length <= 2 ? 2 : 4;
            const formattedChange = change.toFixed(decimalPlaces);
            return change < 0 ? `($${Math.abs(Number(formattedChange))})` : `$${Number(formattedChange).toString()}`;
          }
          return '-';
        };
        const value1 = latest[feedstock] != null ? formatValue(latest[feedstock], feedstock) : '-';
        const value2 = secondLatest[feedstock] != null ? formatValue(secondLatest[feedstock], feedstock) : '-';
        const value3 = thirdLatest && thirdLatest[feedstock] != null ? formatValue(thirdLatest[feedstock], feedstock) : '-';
        const change = isContract ? value3 : formatChange(latest[feedstock], secondLatest[feedstock]);
        return {
          feedstock: formattedFeedstock,
          feedstockKey: feedstock,
          date1: formatDate(latest.date),
          date2: formatDate(secondLatest.date),
          date3: formatDate(thirdLatest?.date),
          value1,
          value2,
          value3,
          change
        };
      }).filter(Boolean) as FeedstockRow[];
    };

    try {
      const [petchemRes, contractsRes] = await Promise.all([
        dataService.getPetchemData(),
        dataService.getContractsData(),
      ]);

      if (!isMountedRef.current) return;
      setFeedstockData(processData(petchemRes, false));
      setContractData(processData(contractsRes, true));

      const toSorted = (resp: any, isContract = false) => Array.isArray(resp)
        ? resp.filter(r => r && typeof r.date === 'string' && (isContract || isWeekday(r.date))).slice().sort((a, b) => String(b.date).localeCompare(String(a.date)))
        : [];
      const spotSorted = toSorted(petchemRes, false);
      const contractSorted = toSorted(contractsRes, true);

      if (spotSorted.length >= 2) {
        setSpotHeader({
          date1: formatSpotDateUTC(spotSorted[0].date),
          date2: formatSpotDateUTC(spotSorted[1].date),
        });
      } else {
        setSpotHeader({ date1: '', date2: '' });
      }

      if (contractSorted.length >= 3) {
        setContractHeader({
          date1: formatContractDateUTC(contractSorted[0].date),
          date2: formatContractDateUTC(contractSorted[1].date),
          date3: formatContractDateUTC(contractSorted[2].date),
        });
      } else if (contractSorted.length === 2) {
        setContractHeader({
          date1: formatContractDateUTC(contractSorted[0].date),
          date2: formatContractDateUTC(contractSorted[1].date),
          date3: '',
        });
      } else {
        setContractHeader({ date1: '', date2: '', date3: '' });
      }

      if (!silent) setNeedsLoading(false);
    } catch (e) {
      if (!isMountedRef.current) return;
      if (!silent) {
        setError('Failed to load feedstock data');
        setNeedsLoading(false);
      } else {
        // Silent failure: keep current data and avoid UI flicker
        // eslint-disable-next-line no-console
        console.debug('Silent feedstock refresh failed', e);
      }
    } finally {
      isFetchingRef.current = false;
    }
  }, [isWeekday]);

  // Initial load
  useEffect(() => {
    loadData();
  }, [loadData]);

  const prevRefreshingRef = useRef(globalRefreshStatus.isRefreshing);
  const prevLastRefreshRef = useRef<number | null>(globalRefreshStatus.lastRefresh ? globalRefreshStatus.lastRefresh.getTime() : null);
  useEffect(() => {
    const nowIsRefreshing = globalRefreshStatus.isRefreshing;
    const lastRefreshTime = globalRefreshStatus.lastRefresh ? globalRefreshStatus.lastRefresh.getTime() : null;

    const wasRefreshing = prevRefreshingRef.current;
    const wasLastRefresh = prevLastRefreshRef.current;

    // Detect transition from refreshing -> not refreshing and a new lastRefresh timestamp
    const finishedNow = wasRefreshing && !nowIsRefreshing;
    const newTimestamp = lastRefreshTime && lastRefreshTime !== wasLastRefresh;

    let timeout: ReturnType<typeof setTimeout> | null = null;
    if (finishedNow && newTimestamp) {
      timeout = setTimeout(() => {
        if (isMountedRef.current) {
          // silent refresh after background refresh completes
          loadData({ silent: true });
        }
      }, 500);
    }

    // Update refs (always)
    prevRefreshingRef.current = nowIsRefreshing;
    prevLastRefreshRef.current = lastRefreshTime;

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [globalRefreshStatus.isRefreshing, globalRefreshStatus.lastRefresh, loadData]);

  // Periodic silent refresh to pick up background updates
  useEffect(() => {
    const REFRESH_INTERVAL_MS = 60 * 1000; // 1 minute

    const doRefresh = () => {
      if (document.visibilityState === 'visible') {
        loadData({ silent: true });
      }
    };

    const onFocus = () => doRefresh();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') doRefresh();
    };

    const intervalId = setInterval(doRefresh, REFRESH_INTERVAL_MS);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [loadData]);

  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const topScrollRef = useRef<HTMLDivElement | null>(null);

  return (
    <div className="w-full">
      {needsLoading ? (
        <div className="text-3xl flex text-rs-off-white items-center justify-center min-h-[200px]">
          <RotateCw className="spinner opacity-30" size={75} strokeWidth={1}/>
        </div>
      ) : error ? (
        <div className="text-center text-red-500 py-4">{error}</div>
      ) : (
        <div className="text-xs py-2 w-full font-light">
          {/* Spot Prices */}
          <div data-testid="feedstock-spot-section" className="flex items-center justify-between mb-2 cursor-pointer" title="Feedstock Spot Prices" onClick={handleSpotPriceClick}>
            <div className="font-normal text-sm">Feedstock Spot Prices</div>
            <div><i className="material-symbols-outlined ml-3">arrow_forward</i></div>
          </div>
          <div data-testid="feedstock-spot-header" className="grid grid-cols-[115px_1fr_1fr_1fr] md:grid-cols-[115px_45px_45px_45px] gap-4 md:text-center text-right border-b-1 border-b-rs-jet py-1">
            <div></div>
            <div>{spotHeader.date2}</div>
            <div>{spotHeader.date1}</div>
            <div>(CHG)</div>
          </div>
          {feedstockData.map((row, idx) => (
            <div
              key={idx}
              data-testid={`feedstock-spot-row-${row.feedstockKey}`}
              className="cursor-pointer hover:bg-rs-jet grid grid-cols-[115px_1fr_1fr_1fr] md:grid-cols-[115px_45px_45px_45px] gap-4 text-left border-b-1 border-b-rs-jet py-1"
              title={`Click to view ${row.feedstock} prices`}
              onClick={handleSpotPriceClick}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSpotPriceClick(); }}
            >
              <div className="text-left cursor-pointer">{row.feedstock}</div>
              <div className="text-right">{row.value2}</div>
              <div className="text-right">{row.value1}</div>
              <div className={`text-right ${row.change.includes('(') ? 'text-rs-red' : row.change.includes('$') ? 'text-rs-dark-green' : ''}`}>{row.change}</div>
            </div>
          ))}

          {/* Contract Prices */}
          <div data-testid="feedstock-contract-section" className="flex items-center justify-between mt-5 mb-2 cursor-pointer" title="Feedstock Contract Prices" onClick={handleContractPriceClick}>
            <div className="font-normal text-sm">Feedstock Contract Prices</div>
            <div><i className="material-symbols-outlined ml-3">arrow_forward</i></div>
          </div>
          <div className="grid grid-cols-[115px_1fr_1fr_1fr] md:grid-cols-[115px_45px_45px_45px] gap-4 text-center border-b-1 border-b-rs-jet py-1">
            <div></div>
            <div>{contractHeader.date3}</div>
            <div>{contractHeader.date2}</div>
            <div>{contractHeader.date1}</div>
          </div>
          {contractData.filter(row => row.feedstock).map((row, idx) => (
            <div
              key={idx}
              data-testid={`feedstock-contract-row-${row.feedstockKey}`}
              className="cursor-pointer hover:bg-rs-jet grid grid-cols-[115px_1fr_1fr_1fr] md:grid-cols-[115px_45px_45px_45px] gap-4 text-left border-b-1 border-b-rs-jet py-1"
              title={`Click to view ${row.feedstock} prices`}
              onClick={handleContractPriceClick}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') handleContractPriceClick(); }}
            >
              <div className="text-left cursor-pointer">{row.feedstock}</div>
              <div className="text-right">{row.value3}</div>
              <div className="text-right">{row.value2}</div>
              <div className="text-right">{row.value1}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FeedstockDaily;
